/**
 * VoicemailModule — Dashboard card showing top 5 unlistened voicemails.
 *
 * - Pulls from /api/telzio/voicemails (filter unlistened, limit 5)
 * - Inline play (audio stream proxy) + click-to-call back via shared
 *   `ventas:softphone:call` event (currently surfaces tel: handler)
 * - Shows "Connect Telzio" CTA if user hasn't connected yet
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Voicemail, Phone, Play, Pause, ArrowRight, Plug } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { authFetch } from '../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;
const MAX = 5;

const fmtDur = (s) => {
  if (!s || s < 1) return '';
  const m = Math.floor(s / 60), sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    const isYesterday = d.toDateString() === new Date(now.getTime() - 86400000).toDateString();
    if (isToday) return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    if (isYesterday) return 'Yesterday';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return iso; }
};

const callerLabel = (caller) => {
  if (!caller) return 'Unknown';
  if (typeof caller === 'string') return caller;
  if (caller.user) {
    const u = caller.user;
    return [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.username || 'User';
  }
  if (caller.phone_number) {
    const p = caller.phone_number;
    return p.cnam || p.local_format || p.e164 || p.raw || 'Unknown';
  }
  return 'Unknown';
};

const callerE164 = (caller) => {
  if (!caller) return null;
  if (typeof caller === 'string') return caller;
  return caller.phone_number?.e164 || caller.phone_number?.raw || null;
};

const VoicemailModule = () => {
  const [connected, setConnected] = useState(null); // null = loading
  const [voicemails, setVoicemails] = useState([]);
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);
  const audioUrlRef = useRef({}); // id -> blob url cache

  const load = useCallback(async () => {
    try {
      const sRes = await authFetch(`${API}/api/telzio/status`);
      if (!sRes.ok) { setConnected(false); return; }
      const status = await sRes.json();
      if (!status.connected) { setConnected(false); return; }
      setConnected(true);
      const vRes = await authFetch(`${API}/api/telzio/voicemails?limit=20`);
      if (!vRes.ok) return;
      const data = await vRes.json();
      const all = data.data || data.items || [];
      // Default: unlistened first; fall back to top 5 of any if there are no unread
      const unread = all.filter((vm) => (vm.status || '').toLowerCase() === 'unlistened');
      setVoicemails((unread.length ? unread : all).slice(0, MAX));
    } catch { /* */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const togglePlay = async (vm) => {
    const id = vm.voicemail_id || vm.id;
    if (playingId === id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    let url = audioUrlRef.current[id];
    if (!url) {
      try {
        const res = await authFetch(`${API}/api/telzio/voicemails/${id}/audio`);
        if (!res.ok) return;
        const blob = await res.blob();
        url = URL.createObjectURL(blob);
        audioUrlRef.current[id] = url;
      } catch { return; }
    }
    if (audioRef.current) {
      audioRef.current.src = url;
      audioRef.current.play();
      setPlayingId(id);
    }
  };

  const callBack = (vm) => {
    const e164 = callerE164(vm.from);
    if (!e164) return;
    window.dispatchEvent(new CustomEvent('ventas:softphone:call', {
      detail: { e164, label: callerLabel(vm.from) },
    }));
  };

  return (
    <section className="rounded-md border border-border-subtle bg-white shadow-xs" data-testid="dashboard-voicemail-module">
      <div className="flex items-center justify-between border-b border-border-subtle px-5 py-4">
        <h3 className="text-sm font-semibold text-text-primary flex items-center gap-2">
          <Voicemail size={16} className="text-brand" />
          Voicemail
          {voicemails.length > 0 && (
            <span className="text-xs font-medium bg-brand/10 text-brand px-2 py-0.5 rounded-full">{voicemails.length}</span>
          )}
        </h3>
        <Link
          to="/internal/client-management/calls"
          className="text-xs font-medium text-brand hover:text-brand-hover flex items-center gap-1"
          data-testid="vm-module-view-all"
        >
          All calls <ArrowRight size={12} />
        </Link>
      </div>
      <div className="p-4">
        {connected === false ? (
          <div className="py-3 text-center">
            <p className="text-sm text-text-secondary mb-2">Telzio not connected</p>
            <Link to="/internal/client-management/calls">
              <Button size="sm" className="bg-brand hover:bg-brand-hover" data-testid="vm-module-connect">
                <Plug size={12} className="mr-1.5" /> Connect Telzio
              </Button>
            </Link>
          </div>
        ) : connected === null ? (
          <p className="text-sm text-text-muted py-3 text-center">Loading…</p>
        ) : voicemails.length === 0 ? (
          <p className="text-sm text-text-muted py-4 text-center">No new voicemails</p>
        ) : (
          <div className="space-y-1.5">
            {voicemails.map((vm) => {
              const id = vm.voicemail_id || vm.id;
              const isPlaying = playingId === id;
              const isUnread = (vm.status || '').toLowerCase() === 'unlistened';
              const e164 = callerE164(vm.from);
              return (
                <div
                  key={id}
                  className={`flex items-center gap-3 p-2.5 rounded-md transition-colors ${isUnread ? 'bg-brand/5 hover:bg-brand/10' : 'hover:bg-surface-page'}`}
                  data-testid={`vm-module-item-${id}`}
                >
                  <button
                    onClick={() => togglePlay(vm)}
                    className="w-8 h-8 rounded-full bg-brand/10 hover:bg-brand/20 flex items-center justify-center flex-shrink-0 text-brand transition-colors"
                    title="Play voicemail"
                    data-testid={`vm-module-play-${id}`}
                  >
                    {isPlaying ? <Pause size={12} /> : <Play size={12} />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-medium text-text-primary truncate">
                        {callerLabel(vm.from)}
                      </p>
                      {isUnread && <span className="text-[9px] font-bold uppercase tracking-wider text-brand">New</span>}
                    </div>
                    <p className="text-[11px] text-text-tertiary">
                      {fmtDate(vm.created || vm.date)}
                      {vm.duration ? ` · ${fmtDur(vm.duration)}` : ''}
                    </p>
                  </div>
                  {e164 && (
                    <button
                      onClick={() => callBack(vm)}
                      className="px-2 py-1 rounded text-[11px] font-medium bg-brand/10 text-brand hover:bg-brand/20 flex items-center gap-1 flex-shrink-0 transition-colors"
                      data-testid={`vm-module-call-${id}`}
                      title={`Call ${e164}`}
                    >
                      <Phone size={10} /> Call
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      {/* Hidden audio element shared across rows */}
      <audio
        ref={audioRef}
        onEnded={() => setPlayingId(null)}
        onPause={() => setPlayingId((id) => (audioRef.current?.paused ? null : id))}
        className="hidden"
      />
    </section>
  );
};

export default VoicemailModule;
